import {
	DocumentViewContainer,
	PreviewFilesContainer,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/ReviewFiles/styled';
import { DataKYCFileTctWithFile, GetKYCInvestorInstanceRes } from '@dealstack/shared/lib/generated';
import { Flex } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	ACCEPT_FILES_TYPES,
	DropzoneV2,
} from '@dealstack/frontend/shared/components/DropzoneV2/DropzoneV2';
import { useKycFileUploadAndAssociate } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/api/useKycFileUploadAndAssociate';
import { onUploadedAndFileAssociated } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/ReviewFiles/utils/onUploadedAndFileAssociated';
import { useManageKYCController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/useManageKYCController';
import { useSearchParams } from 'react-router-dom';
import {
	Step,
	StepType,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/types';
import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState.tsx';
import {
	SelectedTemplate,
	isFormSubmitted,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/FormsCategoryList/types.ts';
import { KycPreviewer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/ReviewFiles/KycPreviewer';

interface Props {
	currentStep: Step;
	currentInvestor?: GetKYCInvestorInstanceRes;
	selectedKycFile?: DataKYCFileTctWithFile;
	filesForStep: DataKYCFileTctWithFile[];
	selectedTemplate?: SelectedTemplate;
	fileBlob?: Blob;
	fileExtension?: string;
	isFileLoading: boolean;
	scale: number;
	onCurrentPageChange: (page: number) => void;
	onTotalPagesChange: (total: number) => void;
}

export const PreviewFiles = ({
	currentStep,
	currentInvestor,
	selectedKycFile,
	filesForStep,
	selectedTemplate,
	fileBlob,
	fileExtension,
	isFileLoading,
	scale,
	onCurrentPageChange,
	onTotalPagesChange,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const [searchParams] = useSearchParams();
	const kycWorkflowId = searchParams.get('kycWorkflowId') ?? undefined;
	const { getKYCDataRequest } = useManageKYCController(currentInvestor?.id, kycWorkflowId);
	const onUploadedAndFileAssociatedSuccess = onUploadedAndFileAssociated(
		tMipManage,
		getKYCDataRequest
	);

	const isFormStep = currentStep?.type === StepType.FORM;
	const formSubmitted = isFormStep && !!selectedTemplate && isFormSubmitted(selectedTemplate);
	const isFormNotSubmitted = isFormStep && !formSubmitted;

	const { uploadFile, isLoading: isUpdating } = useKycFileUploadAndAssociate({
		selectedKycFile,
		currentInvestor,
		currentStep,
		kycWorkflowId,
		onUploadedAndFileAssociatedSuccess,
	});

	if (isFormNotSubmitted) {
		return (
			<Flex justify="center" align="center" h="100%" w="100%">
				<EmptyState title={tMipManage('kyc.review.no_documents')} icon="es-open-box">
					<DropzoneV2
						onDropFile={(file: File) =>
							uploadFile(file, {
								templateIdOverride: selectedTemplate?.templateId,
							})
						}
						multiple={false}
						loading={isUpdating}
						accept={ACCEPT_FILES_TYPES.imagesAndPdf}
					/>
				</EmptyState>
			</Flex>
		);
	}

	if (!isFormStep && !filesForStep.length) {
		return (
			<Flex justify="center" align="center" h="100%" w="100%">
				<EmptyState title={tMipManage('kyc.review.no_documents')} icon="es-open-box">
					{currentStep?.allowFileUpload && (
						<DropzoneV2
							onDropFile={(file: File) => uploadFile(file)}
							multiple={false}
							loading={isUpdating}
							accept={ACCEPT_FILES_TYPES.imagesAndPdf}
						/>
					)}
				</EmptyState>
			</Flex>
		);
	}

	return (
		<PreviewFilesContainer>
			<DocumentViewContainer>
				<KycPreviewer
					file={fileBlob}
					fileName={
						selectedKycFile?.file?.name ??
						selectedTemplate?.fileName ??
						selectedTemplate?.templateName ??
						'Document'
					}
					fileExtension={fileExtension ?? selectedKycFile?.file?.extension}
					isLoading={isFileLoading}
					scale={scale}
					pageWidth={Math.min(600, window.innerWidth * 0.7)}
					onCurrentPageChange={onCurrentPageChange}
					onTotalPagesChange={onTotalPagesChange}
				/>
			</DocumentViewContainer>
		</PreviewFilesContainer>
	);
};
